import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { validateEmail, validatePassword } from '../utils/auth/validation';

export function useSignupForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const signup = useAuthStore(state => state.signup);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Validate inputs
      if (!validateEmail(email)) {
        setError('有効なメールアドレスを入力してください');
        setIsLoading(false);
        return;
      }

      if (!validatePassword(password)) {
        setError('パスワードは8文字以上で、英数字を含める必要があります');
        setIsLoading(false);
        return;
      }

      if (password !== confirmPassword) {
        setError('パスワードが一致しません');
        setIsLoading(false);
        return;
      }

      // 紹介コードの処理 - 暗号化されたままの状態で渡す
      const trimmedReferralCode = referralCode.trim();

      // Register user
      await signup({ 
        email, 
        password, 
        referralCode: trimmedReferralCode
      });

      try {
        // Redirect to verification page
        navigate('/verify-email', { 
          state: { email },
          replace: true 
        });
      } catch (navError) {
        console.error('Navigation error after signup:', navError);
        // If navigation fails, still show success message
        setError('登録は完了しましたが、ページ遷移に失敗しました。ログインページからログインしてください。');
      }
    } catch (err) {
      if (err instanceof Error) {
        // Handle specific error cases
        if (err.message.includes('already registered') || 
            err.message.includes('already exists') || 
            err.message.includes('user_already_exists')) {
          setError('このメールアドレスは既に登録されています。ログインページからログインしてください。');
        } else if (err.message.includes('Database error saving new user')) {
          setError('アカウント作成中にエラーが発生しました。しばらく待ってから再度お試しください。');
        } else if (err.message.includes('unexpected_failure')) {
          setError('サーバーエラーが発生しました。しばらく待ってから再度お試しください。');
        } else {
          // Provide more user-friendly error messages
          if (err.message.includes('JWT')) {
            setError('認証エラーが発生しました。再度お試しください。');
          } else {
            setError(err.message);
          }
        }
      } else {
        setError('登録に失敗しました');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    email,
    password,
    confirmPassword,
    referralCode,
    error,
    isLoading,
    handleEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value),
    handlePasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value),
    handleConfirmPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value),
    handleReferralCodeChange: (e: React.ChangeEvent<HTMLInputElement>) => setReferralCode(e.target.value),
    setReferralCode,
    handleSubmit
  };
}